import dayjs, { Dayjs } from 'dayjs';
import { AppLanguage } from 'shared/enums';
import { DateUtils } from 'shared/utils';

import {
  EvaluationState,
  ExamLanguage,
  ExamLevel,
  GenderEnum,
  RegistrationKind,
  RegistrationStates,
} from 'enums/app';
import { CodeElement, KoodistoResponse } from 'interfaces/code';
import {
  RegistrationToConfirmDetails,
  RegistrationToConfirmDetailsResponse,
} from 'interfaces/confirmRegistration';
import {
  EvaluationOrderDetails,
  EvaluationOrderDetailsResponse,
  EvaluationOrderRequest,
  ExaminationParts,
  Subtest,
} from 'interfaces/evaluationOrder';
import {
  EvaluationPeriod,
  EvaluationPeriodResponse,
  EvaluationPeriods,
  EvaluationPeriodsResponse,
} from 'interfaces/evaluationPeriod';
import {
  ExamSession,
  ExamSessionResponse,
  ExamSessions,
  ExamSessionsResponse,
  ExamSessionType,
} from 'interfaces/examSessions';
import { FreeRegistrationBasis } from 'interfaces/freeRegistration';
import {
  LoginLinkDetails,
  LoginLinkDetailsResponse,
} from 'interfaces/loginLink';
import { KoskiEducationDTO } from 'interfaces/publicEducation';
import {
  PublicEmailRegistration,
  PublicRegistrationIdentifyPayload,
  PublicRegistrationIdentifyRequest,
  PublicRegistrationInitPayload,
  PublicRegistrationInitRequest,
  PublicSuomiFiRegistration,
} from 'interfaces/publicRegistration';
import {
  ModifyContactDetails,
  PersonDetails,
  PersonDetailsResponse,
} from 'interfaces/userDetails';
import { EvaluationOrderState } from 'redux/reducers/evaluationOrder';

export class SerializationUtils {
  static deserializeStartTime(date?: string) {
    if (date) {
      return dayjs(date, 'YYYY-MM-DD').hour(10);
    }
  }

  static deserializeEndTime(date?: string) {
    if (date) {
      return dayjs(date, 'YYYY-MM-DD').hour(16);
    }
  }

  static deserializeExamSessionResponse(
    examSessionResponse: ExamSessionResponse,
  ): ExamSession {
    const dates = {
      session_date: dayjs(examSessionResponse.session_date),
      registration_start_date: SerializationUtils.deserializeStartTime(
        examSessionResponse.registration_start_date,
      ) as Dayjs,
      registration_end_date: SerializationUtils.deserializeEndTime(
        examSessionResponse.registration_end_date,
      ) as Dayjs,
    };

    if (examSessionResponse.type === 'READ_SPEAK') {
      return { ...examSessionResponse, ...dates };
    } else if (examSessionResponse.type === 'LISTEN_WRITE') {
      return { ...examSessionResponse, ...dates };
    } else {
      return { ...examSessionResponse, ...dates };
    }
  }

  static deserializeExamSessionsResponse(
    examSessionsResponse: ExamSessionsResponse,
  ): ExamSessions {
    const exam_sessions = examSessionsResponse.exam_sessions.map(
      SerializationUtils.deserializeExamSessionResponse,
    );

    return { exam_sessions };
  }

  static deserializeEvaluationPeriodResponse(
    evaluationPeriodResponse: EvaluationPeriodResponse,
  ): EvaluationPeriod {
    return {
      ...evaluationPeriodResponse,
      exam_date: dayjs(evaluationPeriodResponse.exam_date),
      evaluation_start_date: dayjs(
        evaluationPeriodResponse.evaluation_start_date,
      ),
      evaluation_end_date: dayjs(evaluationPeriodResponse.evaluation_end_date),
    };
  }

  static deserializeEvaluationPeriodsResponse(
    evaluationPeriodsResponse: EvaluationPeriodsResponse,
  ): EvaluationPeriods {
    const evaluation_periods = evaluationPeriodsResponse.evaluation_periods.map(
      SerializationUtils.deserializeEvaluationPeriodResponse,
    );

    return { evaluation_periods };
  }

  static serializeEvaluationSubtests(
    examinationParts: ExaminationParts,
  ): Array<Subtest> {
    const subtests: Array<Subtest> = [];
    if (examinationParts.readingComprehension) {
      subtests.push('READING');
    }
    if (examinationParts.speaking) {
      subtests.push('SPEAKING');
    }
    if (examinationParts.speechComprehension) {
      subtests.push('LISTENING');
    }
    if (examinationParts.writing) {
      subtests.push('WRITING');
    }

    return subtests;
  }

  static serializeEvaluationOrder({
    examinationParts,
    participantDetails,
  }: EvaluationOrderState): EvaluationOrderRequest {
    return {
      first_names: participantDetails.firstNames as string,
      last_name: participantDetails.lastName as string,
      birthdate: DateUtils.serializeDate(
        DateUtils.parseDateString(participantDetails.birthdate),
      ) as string,
      email: participantDetails.email as string,
      subtests:
        SerializationUtils.serializeEvaluationSubtests(examinationParts),
    };
  }

  static deserializeEvaluationOrderDetailsResponse(
    response: EvaluationOrderDetailsResponse,
  ): EvaluationOrderDetails {
    return { ...response, exam_date: dayjs(response.exam_date) };
  }

  static serializeAppLanguage(appLanguage: AppLanguage) {
    switch (appLanguage) {
      case AppLanguage.Finnish:
        return 'fi';
      case AppLanguage.Swedish:
        return 'sv';
      case AppLanguage.English:
        return 'en';
    }
  }

  static deserializeAppLanguage(language: string): AppLanguage {
    switch (language) {
      case 'sv':
        return AppLanguage.Swedish;
      case 'en':
        return AppLanguage.English;
      case 'fi':
      default:
        return AppLanguage.Finnish;
    }
  }

  static serializeGender(gender?: GenderEnum) {
    switch (gender) {
      case GenderEnum.Male:
        return '1';
      case GenderEnum.Female:
        return '2';
      default:
        return '';
    }
  }

  static deserializeKoodistoResponse(
    response: KoodistoResponse,
  ): Array<CodeElement> {
    return response
      .map((v) =>
        v.metadata.map((metadata) => ({
          code: v.koodiArvo,
          name: metadata.nimi,
          language:
            metadata.kieli === 'EN'
              ? AppLanguage.English
              : metadata.kieli === 'SV'
                ? AppLanguage.Swedish
                : AppLanguage.Finnish,
        })),
      )
      .flat();
  }

  static serializeRegistrationForm(
    registration: Partial<PublicSuomiFiRegistration & PublicEmailRegistration>,
    nationalities: Array<CodeElement>,
  ) {
    const nationality = registration.nationality;
    const nationality_desc = nationalities.find(
      (v) => v.code === nationality && v.language === AppLanguage.Finnish,
    )?.name;

    return {
      first_name: registration.firstNames,
      last_name: registration.lastName,
      preferred_name: registration.preferredName,
      nationalities: [nationality],
      nationality_desc,
      native_language: registration.nativeLanguage,
      certificate_lang: registration.certificateLanguage,
      exam_lang: registration.instructionLanguage,
      birthdate: DateUtils.serializeDate(
        DateUtils.parseDateString(registration.dateOfBirth),
      ),
      ssn: registration.ssn,
      zip: registration.postNumber,
      post_office: registration.postOffice,
      street_address: registration.address,
      phone_number: registration.phoneNumber,
      email: registration.email,
      gender: SerializationUtils.serializeGender(registration.gender),
      country_code: registration.countryCode,
    };
  }

  static deserializeRegistrationState(state: string) {
    switch (state) {
      case 'COMPLETED':
        return RegistrationStates.Completed;
      case 'SUBMITTED':
        return RegistrationStates.Submitted;
      case 'STARTED':
        return RegistrationStates.Started;
      case 'EXPIRED':
        return RegistrationStates.Expired;
      case 'CANCELLED':
        return RegistrationStates.Cancelled;
      case 'PAID_AND_CANCELLED':
        return RegistrationStates.PaidAndCancelled;
      default:
        return RegistrationStates.Unknown;
    }
  }

  static deserializePersonDetails(
    response: PersonDetailsResponse,
  ): PersonDetails {
    return {
      firstName: response.first_name,
      lastName: response.last_name,
      email: response.email,
      phoneNumber: response.phone_number,
      streetAddress: response.street_address,
      postOffice: response.post_office,
      zip: response.zip,
      countryCode: response.country_code,
      registrations: response.registrations?.map((v) => ({
        id: v.id,
        partialExamType: v.partial_exam_type,
        type: v.type as ExamSessionType,
        kind: v.kind as RegistrationKind,
        examSessionId: v.exam_session_id,
        examLang: v.language_code as ExamLanguage,
        examLevel: v.level_code as ExamLevel,
        state: SerializationUtils.deserializeRegistrationState(v.state),
        examDate: dayjs(v.exam_date),
        registrationStartDate: dayjs(v.registration_start_date),
        registrationEndDate: dayjs(v.registration_end_date),
        location: v.location,
        isCancellable: v.is_cancellable,
        isTransfered: v.is_transfered,
        expiresAt: v.expires_at ? dayjs(v.expires_at) : undefined,
        paidAt: v.paid_at ? dayjs(v.paid_at) : undefined,
        examFee: v.exam_fee,
        liftedFromQueueAt: v.lifted_from_queue_at
          ? dayjs(v.lifted_from_queue_at)
          : undefined,
        positionInQueue:
          v.kind === RegistrationKind.Queue
            ? (v.position_in_queue || 0) + 1
            : undefined,
        isFreeRegistration: v.is_free_registration,
        evaluationState: v.evaluation_state
          ? (v.evaluation_state as EvaluationState)
          : undefined,
        start_time_read_listen: v.start_time_read_listen,
        start_time_speak_write: v.start_time_speak_write,
      })),
    };
  }

  static deserializeRegistrationToConfirmDetailsResponse(
    response: RegistrationToConfirmDetailsResponse,
  ): RegistrationToConfirmDetails {
    return {
      ...response,
      session_date: dayjs(response.session_date),
      due_date: dayjs(response.expires_at).subtract(1, 'day'),
      registration_start_date: SerializationUtils.deserializeStartTime(
        response.registration_start_date,
      ) as Dayjs,
      registration_end_date: SerializationUtils.deserializeEndTime(
        response.registration_end_date,
      ) as Dayjs,
    };
  }

  static deserializeLoginLinkDetailsResponse(
    response: LoginLinkDetailsResponse,
  ): LoginLinkDetails {
    return {
      expires_at: dayjs(response.expires_at).subtract(1, 'day'),
    };
  }

  static serializePublicRegistrationInitRequest(
    payload: PublicRegistrationInitPayload,
  ): PublicRegistrationInitRequest {
    return {
      exam_session_id: payload.examSessionId,
      to_queue: payload.registrationKind === RegistrationKind.Queue,
      partial_exam_type: payload.partialExamType,
    };
  }

  static serializePublicRegistrationIdentifyRequest(
    payload: PublicRegistrationIdentifyPayload,
  ): PublicRegistrationIdentifyRequest {
    return {
      exam_session_id: payload.examSessionId,
      to_queue: payload.registrationKind === RegistrationKind.Queue,
      registration_id: payload.registrationId,
    };
  }

  static serializeModifyContactDetailsRequest(payload: ModifyContactDetails) {
    const { email, phoneNumber, streetAddress, zip, postOffice, countryCode } =
      payload;

    return {
      email,
      phone_number: phoneNumber,
      street_address: streetAddress,
      zip,
      post_office: postOffice,
      country_code: countryCode,
    };
  }

  static mapKoskiEducationToFreeRegistrationBasis(
    koskiEducation: KoskiEducationDTO,
  ): FreeRegistrationBasis {
    switch (koskiEducation.educationType) {
      case 'ylioppilastutkinto':
        return 'MatriculationExam';
      case 'dia':
      case 'eb':
        return 'ComparableMatriculation';
      case 'korkeakoulutus':
        return koskiEducation.isActive
          ? 'HigherEducationEnrolled'
          : 'HigherEducationConcluded';
    }
  }
}
